import {
    useState,
    useMemo,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    View
} from "react-native";
import type IStepperProps from "./type";
import type {
    StepperContextType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import StepperConnector from "./components/connector";
import StepperItem from "./components/item";
import StepperList from "./components/list";
import StepperMarker from "./components/marker";
import StepperPanel from "./components/panel";
import StepperStep from "./components/step";
import StepperSummary from "./components/summary";
import StepperText from "./components/text";
import {
    StepperContext
} from "./context";
import {
    STEPPER_EMPTY_STEPS,
    getStepperSteps
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";

const Stepper: FC<IStepperProps> = ({
    steps = STEPPER_EMPTY_STEPS,
    orientation = "horizontal",
    compactBehaviour = "auto",
    "aria-label": ariaLabel,
    accessibilityLabel,
    activeIndex = 0,
    isLinear = true,
    size = "medium",
    customLocalize,
    customTheme,
    onStepPress,
    children,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        availableWidth,
        setAvailableWidth
    ] = useState(0);

    const resolvedSteps = useMemo(() => getStepperSteps({
        isPressEnabled: !!onStepPress,
        activeLocale,
        activeIndex,
        isLinear,
        localize,
        steps
    }), [
        activeLocale,
        activeIndex,
        onStepPress,
        localize,
        isLinear,
        steps
    ]);

    const isCompact = orientation === "horizontal" && compactBehaviour === "auto" && availableWidth > 0 && availableWidth < shapeConfigs.compactBreakpoint;

    const contextValue = useMemo<StepperContextType>(() => ({
        steps: resolvedSteps,
        isInSheet: false,
        customLocalize,
        activeLocale,
        activeIndex,
        customTheme,
        onStepPress,
        orientation,
        size
    }), [
        resolvedSteps,
        customLocalize,
        activeLocale,
        onStepPress,
        orientation,
        customTheme,
        activeIndex,
        size
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const onLayout = (event: LayoutChangeEvent) => {
        setAvailableWidth(Math.floor(event.nativeEvent.layout.width));
    };

    return <StepperContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            aria-label={ariaLabel ?? accessibilityLabel ?? localize("stepper-label")}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            onLayout={onLayout}
            role="navigation"
        >
            {children ?? (isCompact ? <StepperSummary /> : <StepperList />)}
        </View>
    </StepperContext.Provider>;
};
export default Object.assign(Stepper, {
    Connector: StepperConnector,
    Summary: StepperSummary,
    Marker: StepperMarker,
    Panel: StepperPanel,
    Item: StepperItem,
    List: StepperList,
    Step: StepperStep,
    Text: StepperText
});
